import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Minimal Register page that posts to /api/auth/register
 * Fields: firstName, lastName (optional), email, phoneNo (WhatsApp), password,
 * paymentType: "full" | "split", amountPaid
 *
 * It computes balanceToPay and submits the payload the training backend expects.
 */

type PaymentType = "full" | "split";

const TOTAL_PRICE = 49999;
const SPLIT_AMOUNT = 24999; // per the spec; balance = TOTAL_PRICE - amountPaid

const RegisterPage: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [password, setPassword] = useState("");
  const [paymentType, setPaymentType] = useState<PaymentType>("full");
  const [amountPaid, setAmountPaid] = useState<number>(TOTAL_PRICE);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  function updatePaymentType(pt: PaymentType) {
    setPaymentType(pt);
    if (pt === "full") {
      setAmountPaid(TOTAL_PRICE);
    } else {
      setAmountPaid(SPLIT_AMOUNT);
    }
  }

  function computeBalance() {
    const bal = Math.max(0, TOTAL_PRICE - (amountPaid || 0));
    return bal;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);

    // Basic validation
    if (!firstName.trim() || !email.trim() || !phoneNo.trim() || !password) {
      setMessage("Please fill first name, email, WhatsApp number and password.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setMessage("Please enter a valid email.");
      return;
    }

    const payload = {
      email: email.toLowerCase(),
      password,
      firstName,
      lastName,
      phoneNo,
      // Payment fields matching your User.schema expectations
      amountPaid: Number(amountPaid || 0),
      paymentType: paymentType === "full" ? "full" : "split",
      balanceToPay: computeBalance(),
      // include 'course' in metadata (training side resolves course)
      course: "Digital Income Blueprint",
    };

    setLoading(true);

    try {
      const resp = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();

      if (!resp.ok) {
        const err = data?.error || data?.message || resp.statusText;
        setMessage(String(err));
        setLoading(false);
        return;
      }

      // registration success - server may return ok:true and userId
      setMessage("Registration successful. Check your email for login details.");
      setLoading(false);

      // optionally redirect to login after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 1400);
    } catch (err: any) {
      setMessage(err?.message || "Network error");
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl p-6 shadow">
          <h1 className="text-2xl font-bold mb-2">Register — Digital Income Blueprint</h1>
          <p className="text-sm text-slate-600 mb-4">
            Minimal registration for the training platform. Choose a payment option below.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">First name</label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Last name (optional)</label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-md"
                type="email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">WhatsApp number</label>
              <input
                value={phoneNo}
                onChange={(e) => setPhoneNo(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-md"
                placeholder="+2348123456789"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Create a password</label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-md"
                type="password"
                minLength={8}
                required
              />
              <p className="text-xs text-slate-500 mt-1">Password must be at least 8 characters.</p>
            </div>

            {/* Payment options */}
            <div className="bg-slate-50 p-4 rounded-md">
              <div className="flex items-center gap-3 mb-3">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="paymentType"
                    checked={paymentType === "full"}
                    onChange={() => updatePaymentType("full")}
                  />
                  <span className="ml-1 font-medium">Pay once — ₦49,999</span>
                </label>
              </div>

              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="paymentType"
                    checked={paymentType === "split"}
                    onChange={() => updatePaymentType("split")}
                  />
                  <span className="ml-1 font-medium">Split payment — ₦24,999 (first installment)</span>
                </label>
              </div>

              <div className="mt-3">
                <label className="block text-sm text-slate-700">Amount to pay today (NGN)</label>
                <input
                  type="number"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(Number(e.target.value))}
                  className="mt-1 w-48 px-3 py-2 border rounded-md"
                />
                <div className="text-sm text-slate-600 mt-1">
                  Balance to pay: <strong>₦{computeBalance().toLocaleString()}</strong>
                </div>
              </div>
            </div>

            {message && <div className="text-sm text-red-600">{message}</div>}

            <div className="flex justify-between items-center">
              <div className="text-sm text-slate-600">Secure payments via Paystack/Flutterwave on checkout.</div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-[#1B2951] text-white rounded-md font-semibold"
              >
                {loading ? "Registering..." : "Register & Pay"}
              </button>
            </div>
          </form>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          By registering you agree to the program terms. A 30-day money-back guarantee applies.
        </p>
      </div>
    </main>
  );
};

export default RegisterPage;
